import * as XLSX from 'xlsx';
import { Ticket } from '../types';

interface Column {
  id: string;
  label: string;
  visible: boolean;
  render?: (ticket: Ticket) => string | number | boolean;
}

interface ExportOptions {
  tickets: Ticket[];
  visibleColumns: Column[];
  filename?: string;
  sheetName?: string;
  filters?: {
    searchTerm?: string;
    statusFilter?: string;
    priorityFilter?: string;
    currentView?: string;
  };
  // Helper functions to calculate complex values
  getLastCustomerMessage?: (ticket: Ticket) => any;
  getLastAgentMessage?: (ticket: Ticket) => any;
  formatRelativeTime?: (dateString: string) => string;
}

// Helper function to extract plain text from rendered content
const extractTextValue = (
  ticket: Ticket, 
  columnId: string, 
  helpers?: {
    getLastCustomerMessage?: (ticket: Ticket) => any;
    getLastAgentMessage?: (ticket: Ticket) => any;
    formatRelativeTime?: (dateString: string) => string;
  }
): string | number | boolean => {
  switch (columnId) {
    case 'urgency':
      return 'N/A'; // Urgency is a visual indicator, not text
    case 'warning':
      return 'N/A'; // Warning is a visual indicator, not text
    case 'ticket':
      return `${ticket.title}\n#${ticket.ticketNumber || ticket.id}\n${ticket.description}`;
    case 'customer':
      return ticket.customerName || ticket.customer?.name || 'Not provided';
    case 'company':
      return ticket.customerCompany || 'Not provided';
    case 'status':
      return ticket.status;
    case 'priority':
      return ticket.priority;
    case 'agent':
      return ticket.agent ? ticket.agent.name : 'Unassigned';
    case 'lastCustomerMsg':
      if (helpers?.getLastCustomerMessage && helpers?.formatRelativeTime) {
        const lastMsg = helpers.getLastCustomerMessage(ticket);
        return lastMsg ? helpers.formatRelativeTime(lastMsg.createdAt) : 'No messages';
      }
      return 'N/A';
    case 'lastAgentMsg':
      if (helpers?.getLastAgentMessage && helpers?.formatRelativeTime) {
        const lastMsg = helpers.getLastAgentMessage(ticket);
        return lastMsg ? helpers.formatRelativeTime(lastMsg.createdAt) : 'No messages';
      }
      return 'N/A';
    case 'created':
      return new Date(ticket.createdAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    case 'customerCountry':
      return ticket.customerCountry || 'Not provided';
    case 'customerStreetAddress':
      return ticket.customerStreetAddress || ticket.customerAddress || 'Not provided';
    case 'customerCity':
      return ticket.customerCity || 'Not provided';
    case 'customerState':
      return ticket.customerState || 'Not provided';
    case 'customerEmail':
      return ticket.customerEmail || 'Not provided';
    case 'customerPhone':
      return ticket.customerPhone || 'Not provided';
    case 'customerType':
      return ticket.customerType || 'Standard';
    case 'deviceModel':
      return ticket.deviceModel || 'Not specified';
    case 'deviceSerialNumber':
      return ticket.deviceSerialNumber || 'Not provided';
    case 'service':
      return ticket.hasServiceWorkflow ? 'YES' : 'NO';
    // Service workflow specific columns
    case 'workflowNumber':
      const workflow = (ticket as any).serviceWorkflows?.[0];
      return workflow?.workflowNumber || workflow?.workflow_number || 'N/A';
    case 'currentStep':
      const workflowInfo = (ticket as any).serviceWorkflows?.[0];
      return workflowInfo?.currentStep || workflowInfo?.current_step || 'N/A';
    case 'workflowStatus':
      const workflowStatusInfo = (ticket as any).serviceWorkflows?.[0];
      return workflowStatusInfo?.status || 'N/A';
    case 'initiatedAt':
      const initiatedWorkflow = (ticket as any).serviceWorkflows?.[0];
      const initiatedAt = initiatedWorkflow?.initiatedAt || initiatedWorkflow?.initiated_at;
      return initiatedAt ? new Date(initiatedAt).toLocaleDateString() : 'N/A';
    case 'completedAt':
      const completedWorkflow = (ticket as any).serviceWorkflows?.[0];
      const completedAt = completedWorkflow?.completedAt || completedWorkflow?.completed_at;
      return completedAt ? new Date(completedAt).toLocaleDateString() : 'N/A';
    case 'cancelledAt':
      const cancelledWorkflow = (ticket as any).serviceWorkflows?.[0];
      const cancelledAt = cancelledWorkflow?.cancelledAt || cancelledWorkflow?.cancelled_at;
      return cancelledAt ? new Date(cancelledAt).toLocaleDateString() : 'N/A';
    case 'repairDescription':
      const repairWorkflow = (ticket as any).serviceWorkflows?.[0];
      const repairStep = repairWorkflow?.steps?.find((step: any) => step.step_number === 6);
      return repairStep?.repairDescription || repairStep?.repair_description || repairStep?.step_data?.repairDescription || repairStep?.step_data?.repair_description || 'N/A';
    case 'diagnosticSummary':
      const diagnosticWorkflow = (ticket as any).serviceWorkflows?.[0];
      const diagnosticStep = diagnosticWorkflow?.steps?.find((step: any) => step.step_number === 4);
      return diagnosticStep?.diagnosticSummary || diagnosticStep?.diagnostic_summary || diagnosticStep?.step_data?.diagnosticSummary || diagnosticStep?.step_data?.diagnostic_summary || 'N/A';
    case 'findingsDescription':
      const findingsWorkflow = (ticket as any).serviceWorkflows?.[0];
      const findingsStep = findingsWorkflow?.steps?.find((step: any) => step.step_number === 4);
      return findingsStep?.findingsDescription || findingsStep?.findings_description || findingsStep?.step_data?.findingsDescription || findingsStep?.step_data?.findings_description || 'N/A';
    case 'quoteNumber':
      const quoteWorkflow = (ticket as any).serviceWorkflows?.[0];
      const quoteStep = quoteWorkflow?.steps?.find((step: any) => step.step_number === 5);
      return quoteStep?.quoteNumber || quoteStep?.quote_number || quoteStep?.step_data?.quoteNumber || quoteStep?.step_data?.quote_number || 'N/A';
    case 'invoiceNumber':
      const invoiceWorkflow = (ticket as any).serviceWorkflows?.[0];
      const invoiceStep = invoiceWorkflow?.steps?.find((step: any) => step.step_number === 5);
      return invoiceStep?.invoiceNumber || invoiceStep?.invoice_number || invoiceStep?.step_data?.invoiceNumber || invoiceStep?.step_data?.invoice_number || 'N/A';
    case 'loanerModel':
      const loanerWorkflow = (ticket as any).serviceWorkflows?.[0];
      const loanerStep = loanerWorkflow?.steps?.find((step: any) => step.step_number === 2);
      return loanerStep?.loanerModel || loanerStep?.loaner_model || loanerStep?.step_data?.loanerModel || loanerStep?.step_data?.loaner_model || 'N/A';
    case 'loanerSerialNumber':
      const loanerSerialWorkflow = (ticket as any).serviceWorkflows?.[0];
      const loanerSerialStep = loanerSerialWorkflow?.steps?.find((step: any) => step.step_number === 2);
      return loanerSerialStep?.loanerSerialNumber || loanerSerialStep?.loaner_serial_number || loanerSerialStep?.step_data?.loanerSerialNumber || loanerSerialStep?.step_data?.loaner_serial_number || 'N/A';
    case 'partsUsed':
      const partsWorkflow = (ticket as any).serviceWorkflows?.[0];
      const partsStep = partsWorkflow?.steps?.find((step: any) => step.step_number === 6);
      const partsUsed = partsStep?.partsUsed || partsStep?.parts_used || partsStep?.step_data?.partsUsed || partsStep?.step_data?.parts_used;
      
      if (!partsUsed) return 'N/A';
      
      // Handle different data formats
      if (typeof partsUsed === 'string') return partsUsed;
      
      if (Array.isArray(partsUsed)) {
        return partsUsed.map((part: any) => {
          if (typeof part === 'string') return part;
          const partName = part.partName || part.part_name || part.description || part.partNumber || part.part_number || '';
          const quantity = part.quantity || part.qty || '1';
          const lotNumber = part.lotNumber || part.lot_number || part.serialNumber || part.serial_number || '';
          return `${partName}, ${quantity}${lotNumber ? `, ${lotNumber}` : ''}`;
        }).filter(Boolean).join('\n');
      }
      
      if (typeof partsUsed === 'object') {
        return Object.entries(partsUsed).map(([key, value]: [string, any]) => {
          if (typeof value === 'string') return value;
          const partName = value?.partName || value?.part_name || value?.description || key;
          const quantity = value?.quantity || value?.qty || '1';
          const lotNumber = value?.lotNumber || value?.lot_number || value?.serialNumber || value?.serial_number || '';
          return `${partName}, ${quantity}${lotNumber ? `, ${lotNumber}` : ''}`;
        }).filter(Boolean).join('\n');
      }
      
      return 'N/A';
    case 'warrantyExpiration':
      const warrantyWorkflow = (ticket as any).serviceWorkflows?.[0];
      const warrantyStep = warrantyWorkflow?.steps?.find((step: any) => step.step_number === 1);
      const warrantyExpiration = warrantyStep?.warrantyExpiration || warrantyStep?.warranty_expiration || warrantyStep?.step_data?.warrantyExpiration || warrantyStep?.step_data?.warranty_expiration;
      return warrantyExpiration ? new Date(warrantyExpiration).toLocaleDateString() : 'N/A';
    case 'warrantyStatus':
      const warrantyStatusWorkflow = (ticket as any).serviceWorkflows?.[0];
      const warrantyStatusStep = warrantyStatusWorkflow?.steps?.find((step: any) => step.step_number === 1);
      const warrantyStatusExpiration = warrantyStatusStep?.warrantyExpiration || warrantyStatusStep?.warranty_expiration || warrantyStatusStep?.step_data?.warrantyExpiration || warrantyStatusStep?.step_data?.warranty_expiration;
      
      if (!warrantyStatusExpiration) return 'Unknown';
      
      const expirationDate = new Date(warrantyStatusExpiration);
      const currentDate = new Date();
      const isExpired = expirationDate < currentDate;
      return isExpired ? 'Expired' : 'Current';
    default:
      return 'N/A';
  }
};

export const exportTicketsToExcel = (options: ExportOptions): void => {
  const {
    tickets,
    visibleColumns,
    filename = 'tickets-export.xlsx',
    sheetName = 'Tickets',
    filters,
    getLastCustomerMessage,
    getLastAgentMessage,
    formatRelativeTime
  } = options;

  try {
    // Filter out the actions column since it's not exportable
    const exportableColumns = visibleColumns.filter(col => col.id !== 'actions');

    // Create headers
    const headers = exportableColumns.map(col => col.label);

    // Create data rows
    const data = tickets.map(ticket => {
      const row: { [key: string]: string | number | boolean } = {};
      const helpers = { getLastCustomerMessage, getLastAgentMessage, formatRelativeTime };
      exportableColumns.forEach(col => {
        row[col.label] = extractTextValue(ticket, col.id, helpers);
      });
      return row;
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const columnWidths = exportableColumns.map(col => {
      switch (col.id) {
        case 'ticket':
          return { width: 50 }; // Wider for title + description
        case 'customerStreetAddress':
          return { width: 30 };
        case 'customer':
        case 'company':
          return { width: 20 };
        case 'created':
          return { width: 18 };
        default:
          return { width: 15 };
      }
    });
    ws['!cols'] = columnWidths;

    // Add metadata sheet with filter information
    const metadataSheet = XLSX.utils.aoa_to_sheet([
      ['Export Information'],
      ['Generated at:', new Date().toLocaleString()],
      ['Total records:', tickets.length],
      [''],
      ['Applied Filters:'],
      ['Search term:', filters?.searchTerm || 'None'],
      ['Status filter:', filters?.statusFilter === 'all' ? 'All' : (filters?.statusFilter || 'All')],
      ['Priority filter:', filters?.priorityFilter === 'all' ? 'All' : (filters?.priorityFilter || 'All')],
      ['Current view:', filters?.currentView || 'All tickets'],
      [''],
      ['Exported Columns:'],
      ...exportableColumns.map(col => ['', col.label])
    ]);

    // Set metadata sheet column widths
    metadataSheet['!cols'] = [{ width: 20 }, { width: 30 }];

    // Add sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.utils.book_append_sheet(wb, metadataSheet, 'Export Info');

    // Generate and download the file
    XLSX.writeFile(wb, filename);

    console.log(`Excel file exported: ${filename}`);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export tickets to Excel. Please try again.');
  }
};

// Helper function to generate filename with timestamp and filters
export const generateExportFilename = (filters?: {
  searchTerm?: string;
  statusFilter?: string;
  priorityFilter?: string;
  currentView?: string;
}): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '');
  let filename = `tickets-export-${timestamp}`;

  if (filters) {
    const parts: string[] = [];
    
    if (filters.currentView && filters.currentView !== 'tickets') {
      parts.push(filters.currentView.replace(/[^a-zA-Z0-9]/g, '-'));
    }
    
    if (filters.statusFilter && filters.statusFilter !== 'all') {
      parts.push(`status-${filters.statusFilter}`);
    }
    
    if (filters.priorityFilter && filters.priorityFilter !== 'all') {
      parts.push(`priority-${filters.priorityFilter}`);
    }
    
    if (filters.searchTerm) {
      const cleanSearch = filters.searchTerm.replace(/[^a-zA-Z0-9]/g, '-').slice(0, 20);
      parts.push(`search-${cleanSearch}`);
    }
    
    if (parts.length > 0) {
      filename += '-' + parts.join('-');
    }
  }

  return filename + '.xlsx';
};

// Generic export function for any data
export const exportToExcel = (data: any[], filename: string = 'export.xlsx', sheetName: string = 'Data'): void => {
  try {
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Set column widths for better readability
    const columnWidths = Object.keys(data[0] || {}).map(() => ({ width: 20 }));
    ws['!cols'] = columnWidths;

    // Add metadata sheet with export information
    const metadataSheet = XLSX.utils.aoa_to_sheet([
      ['Export Information'],
      ['Generated at:', new Date().toLocaleString()],
      ['Total records:', data.length],
      [''],
      ['Data exported from NeuroVirtual Support Platform'],
    ]);

    // Set metadata sheet column widths
    metadataSheet['!cols'] = [{ width: 20 }, { width: 30 }];

    // Add sheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.utils.book_append_sheet(wb, metadataSheet, 'Export Info');

    // Generate and download the file
    XLSX.writeFile(wb, filename);

    console.log(`Excel file exported: ${filename}`);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export data to Excel. Please try again.');
  }
}; 